import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import { FaCheckCircle, FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import PasswordCriteriaList from "../common/components/PasswordCriteriaList";
import { isPasswordValid } from "../common/utils/passwordCriteria";
import { PASSWORD_ALREADY_SET_CODE, setPasswordWithToken, validateSetupToken } from "../common/api/setPasswordApi";

const SET_PASSWORD_SUCCESS_TITLE = "Your account has been activated successfully, and your password has been set.";
const SET_PASSWORD_SUCCESS_MESSAGE = "";

const PAGE_STATE = {
  LOADING: "loading",
  FORM: "form",
  EXPIRED: "expired",
  SUCCESS: "success",
};

const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setupToken = searchParams.get("token") || "";

  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [expiredMessage, setExpiredMessage] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordMeetsCriteria = isPasswordValid(password);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const canSubmit = passwordMeetsCriteria && passwordsMatch && !isSubmitting;

  useEffect(() => {
    let cancelled = false;

    const validateToken = async () => {
      const result = await validateSetupToken(setupToken);
      if (cancelled) {
        return;
      }

      if (result.success) {
        setPageState(PAGE_STATE.FORM);
        return;
      }

      setExpiredMessage(result.message);
      setErrorCode(result.errorCode || "");
      setPageState(PAGE_STATE.EXPIRED);
    };

    validateToken();
    return () => {
      cancelled = true;
    };
  }, [setupToken]);

  const showExpiredState = (message, nextErrorCode = "") => {
    setExpiredMessage(message);
    setErrorCode(nextErrorCode);
    setPageState(PAGE_STATE.EXPIRED);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setPasswordError("");

    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    const result = await setPasswordWithToken({
      setupToken,
      newPassword: password,
      confirmPassword,
    });
    setIsSubmitting(false);

    if (!result.success) {
      if (result.tokenInvalid) {
        showExpiredState(result.message, result.errorCode || "");
        return;
      }
      setPasswordError(result.message);
      return;
    }

    setPassword("");
    setConfirmPassword("");
    setPageState(PAGE_STATE.SUCCESS);
  };

  const handleContinueToLogin = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    const isAndroid = /Android/i.test(userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);

    if (isAndroid) {
      window.location.href =
        "https://play.google.com/store/apps/details?id=com.e2infosystems.bt_caregiver&pcampaignid=web_share";
      return;
    }

    if (isIOS) {
      window.location.href = "https://apps.apple.com/in/app/bt-caregiver/id6482351707";
      return;
    }

    navigate("/login", { replace: true });
  };

  const isPasswordAlreadySet = errorCode === PASSWORD_ALREADY_SET_CODE;
  const isCenteredView = pageState === PAGE_STATE.EXPIRED || pageState === PAGE_STATE.SUCCESS;

  return (
    <div className={`outer-container bt-set-password-outer${isCenteredView ? " bt-set-password-outer--centered" : ""}`}>
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={isSubmitting}>
        <img src={require("../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>

      <div className={`bt-set-password-card${isCenteredView ? " bt-set-password-card--centered" : ""}`}>
        {pageState === PAGE_STATE.LOADING ? (
          <>
            <div className="bt-set-password-logo-wrap">
              <img src={require("../assets/Loginpage_logo.png")} className="bt-set-password-logo" alt="BridgeTree" />
            </div>
            <div className="bt-set-password-loading" role="status" aria-live="polite">
              <p>Validating your link…</p>
            </div>
          </>
        ) : null}

        {pageState === PAGE_STATE.EXPIRED ? (
          <div className="bt-set-password-expired">
            <div className="bt-set-password-logo-wrap bt-set-password-logo-wrap--expired">
              <img src={require("../assets/Loginpage_logo.png")} className="bt-set-password-logo bt-set-password-logo--expired" alt="BridgeTree" />
            </div>
            <h1 className="bt-set-password-title">{isPasswordAlreadySet ? "Password already set" : "Link expired"}</h1>
            <p className="bt-set-password-desc">{expiredMessage}</p>
          </div>
        ) : null}

        {pageState === PAGE_STATE.SUCCESS ? (
          <div className="bt-set-password-success" role="status" aria-live="polite">
            <FaCheckCircle className="bt-set-password-success-icon" aria-hidden="true" />
            <h1 className="bt-set-password-title bt-set-password-success-title">{SET_PASSWORD_SUCCESS_TITLE}</h1>
            {SET_PASSWORD_SUCCESS_MESSAGE ? (
              <p className="bt-set-password-desc bt-set-password-success-message">{SET_PASSWORD_SUCCESS_MESSAGE}</p>
            ) : null}
            <button type="button" className="bt-set-password-submit bt-set-password-success-open-btn" onClick={handleContinueToLogin}>
              Continue to login
            </button>
          </div>
        ) : null}

        {pageState === PAGE_STATE.FORM ? (
          <>
            <header className="bt-set-password-header">
              <img
                src={require("../assets/Loginpage_logo.png")}
                className="bt-set-password-logo bt-set-password-inline-logo"
                alt="BridgeTree"
              />
              <h1 className="bt-set-password-title">Set your password</h1>
              <p className="bt-set-password-desc">Create a permanent password to secure your account. You will use this password the next time you log in.</p>
            </header>

            <form className="bt-set-password-form" onSubmit={handleSubmit} noValidate>
              <div className="bt-set-password-field-group">
                <label htmlFor="set-password-new" className="bt-set-password-label">
                  New password
                </label>
                <div className="bt-login-input-wrap">
                  <FaLock className="bt-login-input-icon" aria-hidden="true" />
                  <input
                    id="set-password-new"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="bt-login-input bt-login-input--with-action bt-set-password-input"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError("");
                    }}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    required
                  />
                  <button type="button" className="bt-login-toggle-visibility" onClick={() => setShowPassword((open) => !open)} aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>
              </div>

              <div className="bt-set-password-field-group">
                <label htmlFor="set-password-confirm" className="bt-set-password-label">
                  Confirm password
                </label>
                <div className="bt-login-input-wrap">
                  <FaLock className="bt-login-input-icon" aria-hidden="true" />
                  <input
                    id="set-password-confirm"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    className="bt-login-input bt-login-input--with-action bt-set-password-input"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setPasswordError("");
                    }}
                    placeholder="Re-enter new password"
                    autoComplete="new-password"
                    required
                  />
                  <button type="button" className="bt-login-toggle-visibility" onClick={() => setShowConfirmPassword((open) => !open)} aria-label={showConfirmPassword ? "Hide password" : "Show password"}>
                    {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>
              </div>

              <PasswordCriteriaList password={password} />

              {passwordError ? (
                <p className="bt-set-password-error" role="alert">
                  {passwordError}
                </p>
              ) : null}

              <button type="submit" className="bt-set-password-submit" disabled={!canSubmit}>
                Set password &amp; continue
              </button>
            </form>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default SetPassword;
